import React, { useState, useEffect, useMemo } from 'react';
import { getAttendanceHistory } from '../services/googleScriptServiceCORS';
import './HistoryPage.css';

function HistoryPage({ employee }) {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.toISOString().slice(0, 7)); // YYYY-MM
  const [attendanceData, setAttendanceData] = useState({}); // { date: [records] }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null); // Date được chọn để hiển thị modal
  const [selectedImage, setSelectedImage] = useState(null); // Ảnh được chọn để xem to
  const [monthStats, setMonthStats] = useState({
    totalHours: 0,
    workingDays: 0,
    noCheckoutDays: 0,
    noAttendanceDays: 0
  });

  useEffect(() => {
    loadMonthData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee, selectedMonth]);

  const loadMonthData = async () => {
    if (!employee || !employee.code) {
      console.error('Employee or employee.code is missing', { employee });
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Tính toán start và end date của tháng
      const year = parseInt(selectedMonth.split('-')[0]);
      const month = parseInt(selectedMonth.split('-')[1]);
      const monthStartDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const monthEndDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;

      const employeeCode = employee.code;
      
      if (!employeeCode) {
        throw new Error('Invalid employee code: ' + employeeCode);
      }
      
      const records = await getAttendanceHistory(employeeCode, monthStartDate, monthEndDate);
      
        // Nhóm records theo ngày và tính số giờ làm việc
        const grouped = {};
        if (Array.isArray(records)) {
          records.forEach(record => {
            // Ưu tiên dùng timestamp để lấy date theo timezone local
            let date = null;
            
            // Nếu có timestamp, dùng timestamp để lấy date theo local timezone
            if (record.timestamp) {
              try {
                const dateObj = new Date(record.timestamp);
                if (!isNaN(dateObj.getTime())) {
                  // Lấy date theo local timezone, không dùng UTC
                  const year = dateObj.getFullYear();
                  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                  const day = String(dateObj.getDate()).padStart(2, '0');
                  date = `${year}-${month}-${day}`;
                }
              } catch (e) {
                console.warn('Error parsing timestamp:', record.timestamp, e);
              }
            }
            
            // Nếu không có timestamp hoặc parse lỗi, thử dùng record.date
            if (!date && record.date) {
              let dateStr = record.date;
            
            // Normalize date to YYYY-MM-DD format
              if (dateStr instanceof Date) {
                // Lấy date theo local timezone
                const year = dateStr.getFullYear();
                const month = String(dateStr.getMonth() + 1).padStart(2, '0');
                const day = String(dateStr.getDate()).padStart(2, '0');
                date = `${year}-${month}-${day}`;
              } else if (typeof dateStr === 'string') {
                // Nếu là string có chứa time (ISO format), chỉ lấy phần date
                if (dateStr.includes('T')) {
                  // Parse theo local timezone
                  const dateObj = new Date(dateStr);
                  if (!isNaN(dateObj.getTime())) {
                    const year = dateObj.getFullYear();
                    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                    const day = String(dateObj.getDate()).padStart(2, '0');
                    date = `${year}-${month}-${day}`;
                  } else {
                    // Fallback: chỉ lấy phần date trước 'T'
                    date = dateStr.split('T')[0];
                  }
                } else if (dateStr.includes(' ')) {
                  // Parse theo local timezone
                  const dateObj = new Date(dateStr);
                  if (!isNaN(dateObj.getTime())) {
                    const year = dateObj.getFullYear();
                    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                    const day = String(dateObj.getDate()).padStart(2, '0');
                    date = `${year}-${month}-${day}`;
                  } else {
                    // Fallback: chỉ lấy phần date trước ' '
                    date = dateStr.split(' ')[0];
                  }
                } else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
                  // Đã đúng format YYYY-MM-DD
                  date = dateStr;
                } else {
                  // Thử parse lại
                  const dateObj = new Date(dateStr);
                  if (!isNaN(dateObj.getTime())) {
                    const year = dateObj.getFullYear();
                    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                    const day = String(dateObj.getDate()).padStart(2, '0');
                    date = `${year}-${month}-${day}`;
                  }
                  }
                }
              }
              
              if (date && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
                if (!grouped[date]) {
                  grouped[date] = {
                    records: [],
                    workHours: null
                  };
                }
                grouped[date].records.push(record);
              } else {
              console.warn('Invalid date format:', record.date, 'timestamp:', record.timestamp, '-> normalized to:', date);
            }
          });
        
        // Tính số giờ làm việc cho mỗi ngày
        Object.keys(grouped).forEach(date => {
          const dayRecords = grouped[date].records;
          
          // Debug: Log tất cả records của ngày (chỉ log nếu có records)
          if (dayRecords.length > 0) {
            console.log(`[HistoryPage] Date ${date}:`, {
              totalRecords: dayRecords.length,
              records: dayRecords.map(r => ({
                type: r.type,
                timestamp: r.timestamp,
                time: r.time,
                date: r.date,
                originalDate: r.date, // Date gốc từ record
                parsedDate: (() => {
                  // Parse date từ timestamp để xem date thực tế
                  if (r.timestamp) {
                    const ts = new Date(r.timestamp);
                    const year = ts.getFullYear();
                    const month = String(ts.getMonth() + 1).padStart(2, '0');
                    const day = String(ts.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                  }
                  return null;
                })(),
                fullRecord: r // Log toàn bộ record để debug
              }))
            });
          }
          
          // Tìm checkin và checkout - Google Apps Script trả về type là "Vào" hoặc "Ra"
          const checkinRecords = dayRecords.filter(r => {
            const type = (r.type || '').toString().trim();
            return type === 'Vào' || type === 'vào' || type.toLowerCase() === 'checkin' || type === 'vao';
          });
          const checkoutRecords = dayRecords.filter(r => {
            const type = (r.type || '').toString().trim();
            return type === 'Ra' || type === 'ra' || type.toLowerCase() === 'checkout';
          });
          
          // Debug: Log số lượng checkin/checkout
          console.log(`[HistoryPage] Date ${date}:`, {
            checkinCount: checkinRecords.length,
            checkoutCount: checkoutRecords.length,
            checkinTypes: checkinRecords.map(r => r.type),
            checkoutTypes: checkoutRecords.map(r => r.type)
          });
          
          // Lấy checkin sớm nhất và checkout muộn nhất (không phải cuối cùng)
          // Sắp xếp theo timestamp để lấy đúng
          const sortedCheckins = checkinRecords.sort((a, b) => {
            const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
            const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
            return timeA - timeB; // Sớm nhất trước
          });
          const sortedCheckouts = checkoutRecords.sort((a, b) => {
            const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
            const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
            return timeB - timeA; // Muộn nhất trước
          });
          
          const checkinRecord = sortedCheckins.length > 0 ? sortedCheckins[0] : null; // Sớm nhất
          const checkoutRecord = sortedCheckouts.length > 0 ? sortedCheckouts[0] : null; // Muộn nhất
          
          // Debug: Log kết quả
          console.log(`[HistoryPage] Date ${date}:`, {
            hasCheckin: !!checkinRecord,
            hasCheckout: !!checkoutRecord,
            checkinTime: checkinRecord?.timestamp || checkinRecord?.time,
            checkoutTime: checkoutRecord?.timestamp || checkoutRecord?.time
          });
          
          if (checkinRecord && checkoutRecord) {
            try {
              // Google Apps Script trả về timestamp là ISO string trong cột A
              const checkinTimeStr = checkinRecord.timestamp;
              const checkoutTimeStr = checkoutRecord.timestamp;
              
              if (checkinTimeStr && checkoutTimeStr) {
                const checkinTime = new Date(checkinTimeStr);
                const checkoutTime = new Date(checkoutTimeStr);
                
                if (!isNaN(checkinTime.getTime()) && !isNaN(checkoutTime.getTime())) {
                  // Kiểm tra nếu cùng giờ (checkin = checkout) → 0h
                  if (checkinTime.getTime() === checkoutTime.getTime()) {
                    grouped[date].workHours = '0';
                  } else {
                    let workHours = (checkoutTime - checkinTime) / (1000 * 60 * 60);
                    
                    // Xử lý trường hợp checkout ngày hôm sau (nếu checkout < checkin về mặt giờ)
                    if (workHours < 0) {
                      // Có thể là checkout ngày hôm sau, thêm 24h
                      workHours = workHours + 24;
                    }
                    
                    if (workHours > 0 && !isNaN(workHours) && workHours < 24) { // Hợp lý: < 24 giờ
                      // Format: làm tròn đến 0.5h gần nhất
                      const roundedHours = Math.round(workHours * 2) / 2;
                      const hours = roundedHours.toFixed(1);
                      grouped[date].workHours = hours.endsWith('.0') ? Math.round(roundedHours).toString() : hours;
                    } else {
                      grouped[date].workHours = null;
                    }
                  }
                } else {
                  grouped[date].workHours = null;
                }
              } else {
                // Thử dùng time field nếu không có timestamp
                const checkinTimeFromTime = checkinRecord.time;
                const checkoutTimeFromTime = checkoutRecord.time;
                
                if (checkinTimeFromTime && checkoutTimeFromTime) {
                  // Parse time string (HH:mm:ss hoặc HH:mm)
                  const parseTime = (timeStr) => {
                    if (!timeStr) return 0;
                    const str = timeStr.toString().trim();
                    const parts = str.split(':');
                    if (parts.length >= 2) {
                      const hours = parseInt(parts[0]) || 0;
                      const minutes = parseInt(parts[1]) || 0;
                      return hours + minutes / 60;
                    }
                    return 0;
                  };
                  
                  const checkinHours = parseTime(checkinTimeFromTime);
                  const checkoutHours = parseTime(checkoutTimeFromTime);
                  
                  // Kiểm tra nếu cùng giờ
                  if (checkinHours === checkoutHours) {
                    grouped[date].workHours = '0';
                  } else {
                    let workHours = checkoutHours - checkinHours;
                    if (workHours < 0) {
                      workHours = workHours + 24; // Ngày hôm sau
                    }
                    
                    if (workHours > 0 && workHours < 24) {
                      const roundedHours = Math.round(workHours * 2) / 2;
                      const hours = roundedHours.toFixed(1);
                      grouped[date].workHours = hours.endsWith('.0') ? Math.round(roundedHours).toString() : hours;
                    } else {
                      grouped[date].workHours = null;
                    }
                  }
                } else {
                  // Thử tạo Date từ date + time
                  try {
                    const checkinDateTime = new Date(`${date}T${checkinRecord.time || '00:00:00'}`);
                    const checkoutDateTime = new Date(`${date}T${checkoutRecord.time || '00:00:00'}`);
                    
                    if (!isNaN(checkinDateTime.getTime()) && !isNaN(checkoutDateTime.getTime())) {
                      let workHours = (checkoutDateTime - checkinDateTime) / (1000 * 60 * 60);
                      
                      if (workHours < 0) {
                        workHours = workHours + 24;
                      }
                      
                      if (workHours > 0 && workHours < 24) {
                        const roundedHours = Math.round(workHours * 2) / 2;
                        const hours = roundedHours.toFixed(1);
                        grouped[date].workHours = hours.endsWith('.0') ? Math.round(roundedHours).toString() : hours;
                      } else {
                        grouped[date].workHours = null;
                      }
                    } else {
                      grouped[date].workHours = null;
                    }
                  } catch (e) {
                    grouped[date].workHours = null;
                  }
                }
              }
            } catch (e) {
              grouped[date].workHours = null;
            }
          } else if (checkinRecord) {
            // Chỉ có checkin trong ngày, không có checkout trong cùng ngày
            // KHÔNG tìm checkout ở các ngày khác để tránh ghép nhầm ca
            grouped[date].workHours = 'Chưa ra';
            console.log(`[HistoryPage] Date ${date}: Has checkin but no checkout in same day -> workHours = 'Chưa ra'`);
          } else {
            // Không có checkin trong ngày → không tính giờ
            grouped[date].workHours = null;
          }
        });
      } else {
        console.warn('Records is not an array:', records);
      }

      setAttendanceData(grouped);
      setError(null);
      
      // Tính toán thống kê tháng
      calculateMonthStats(grouped, year, month);
    } catch (error) {
      console.error('Error loading history:', error);
      setError(error.message || 'Không thể tải lịch sử chấm công');
      setAttendanceData({});
      setMonthStats({
        totalHours: 0,
        workingDays: 0,
        noCheckoutDays: 0,
        noAttendanceDays: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateMonthStats = (grouped, year, month) => {
    const lastDay = new Date(year, month, 0).getDate();
    let totalHours = 0;
    let workingDays = 0;
    let noCheckoutDays = 0;
    let noAttendanceDays = 0;

    for (let day = 1; day <= lastDay; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayData = grouped[dateStr];
      
      if (!dayData || !dayData.records || dayData.records.length === 0) {
        noAttendanceDays++;
        continue;
      }

      const workHours = dayData.workHours;
      if (workHours === 'Chưa ra') {
        noCheckoutDays++;
      } else if (workHours && typeof workHours === 'string' && !workHours.includes('Chưa')) {
        const hours = parseFloat(workHours);
        if (!isNaN(hours) && hours > 0) {
          totalHours += hours;
          workingDays++;
        }
      } else if (workHours && typeof workHours === 'number') {
        totalHours += workHours;
        workingDays++;
      }
    }

    setMonthStats({
      totalHours: Math.round(totalHours * 10) / 10,
      workingDays,
      noCheckoutDays,
      noAttendanceDays
    });
  };

  const handleMonthChange = (direction) => {
    const [year, month] = selectedMonth.split('-').map(Number);
    let newYear = year;
    let newMonth = month + direction;
    
    if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    } else if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    }
    
    setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, '0')}`);
  };

  const handleDateClick = (date) => {
    const dayData = attendanceData[date];
    if (dayData && dayData.records && dayData.records.length > 0) {
      setSelectedDate(date);
    }
  };

  const closeModal = () => {
    setSelectedDate(null);
  };

  const openImageModal = (imageUrl) => {
    setSelectedImage(imageUrl);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
  };

  const getDayRecords = (date) => {
    const dayData = attendanceData[date];
    if (!dayData || !dayData.records) return { checkin: null, checkout: null, records: [] };
    
    const records = dayData.records;
    const checkinRecords = records.filter(r => {
      const type = (r.type || '').toString().trim();
      return type === 'Vào' || type === 'vào' || type.toLowerCase() === 'checkin' || type === 'vao';
    });
    const checkoutRecords = records.filter(r => {
      const type = (r.type || '').toString().trim();
      return type === 'Ra' || type === 'ra' || type.toLowerCase() === 'checkout';
    });
    
    // Chọn checkin hiển thị:
    // - Ưu tiên lần chấm công HỢP LỆ gần nhất trong ngày (note không chứa "Không hợp lệ")
    // - Nếu không có, fallback về checkin đầu tiên trong ngày (cũ nhất)
    const validCheckins = checkinRecords.filter(r => {
      const note = (r.note || '').toString();
      return !note.includes('Không hợp lệ');
    });
    
    const sortByTimestampAsc = (a, b) => {
      const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return timeA - timeB;
    };
    
    const sortByTimestampDesc = (a, b) => {
      const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return timeB - timeA;
    };
    
    let chosenCheckin = null;
    if (validCheckins.length > 0) {
      const sortedValid = [...validCheckins].sort(sortByTimestampDesc); // lấy hợp lệ mới nhất
      chosenCheckin = sortedValid[0];
    } else if (checkinRecords.length > 0) {
      const sortedAll = [...checkinRecords].sort(sortByTimestampAsc); // fallback: sớm nhất
      chosenCheckin = sortedAll[0];
    }
    
    const sortedCheckouts = [...checkoutRecords].sort((a, b) => {
      const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return timeB - timeA; // checkout muộn nhất
    });
    
    return {
      checkin: chosenCheckin,
      checkout: sortedCheckouts.length > 0 ? sortedCheckouts[0] : null,
      records: records
    };
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return timestamp;
    }
  };

  // NOTE: Không dùng formatTime hiện tại; giữ formatDateTime đủ cho UI

  const getWorkHoursStatus = (workHours) => {
    if (!workHours || workHours === 'Chưa ra') return { status: 'no-checkout', color: '#ff9800' };
    const hours = parseFloat(workHours);
    if (isNaN(hours)) return { status: 'unknown', color: '#999' };
    if (hours >= 8) return { status: 'enough', color: '#28a745' };
    if (hours >= 4) return { status: 'partial', color: '#ffc107' };
    return { status: 'low', color: '#dc3545' };
  };

  // Xử lý URL ảnh từ Google Drive
  const getImageUrl = (photoUrl) => {
    if (!photoUrl) return null;
    
    // Nếu là Google Drive link, thử nhiều cách
    if (photoUrl.includes('drive.google.com')) {
      // Cách 1: Lấy FILE_ID từ URL
      let fileId = null;
      
      // Format: https://drive.google.com/file/d/FILE_ID/view
      const match1 = photoUrl.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
      if (match1 && match1[1]) {
        fileId = match1[1];
      }
      
      // Format: https://drive.google.com/open?id=FILE_ID
      if (!fileId) {
        const match2 = photoUrl.match(/[?&]id=([a-zA-Z0-9_-]+)/);
        if (match2 && match2[1]) {
          fileId = match2[1];
        }
      }
      
      if (fileId) {
        // Thử dùng thumbnail với size lớn (sz=w2000 = width 2000px)
        // Nếu file được share public, cách này sẽ work
        return `https://drive.google.com/thumbnail?id=${fileId}&sz=w2000`;
      }
    }
    
    // Nếu đã là direct link hoặc URL khác, dùng trực tiếp
    return photoUrl;
  };

  // Tính toán calendar days - sử dụng useMemo để re-render khi attendanceData thay đổi
  // PHẢI đặt TRƯỚC early return để tuân thủ Rules of Hooks
  const calendarDays = useMemo(() => {
    const [year, month] = selectedMonth.split('-').map(Number);
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay(); // 0 = Sunday, 1 = Monday, etc.
    
    // Điều chỉnh để Chủ nhật = 0, Thứ 2 = 1, ...
    const adjustedStartingDay = startingDayOfWeek;

    const days = [];

    // Thêm các ô trống trước ngày đầu tiên
    for (let i = 0; i < adjustedStartingDay; i++) {
      days.push(null);
    }

    // Thêm các ngày trong tháng
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      
      // Tìm data cho ngày này - dùng trực tiếp key dateStr (đã được normalize theo local timezone)
      // Không dùng toISOString() để tránh lệch ngày do UTC
      const dayData = attendanceData[dateStr];
      
      // dayData có thể là object {records: [], workHours: ...} hoặc array (backward compatibility)
      const hasAttendance = dayData && (
        (Array.isArray(dayData) && dayData.length > 0) ||
        (dayData.records && Array.isArray(dayData.records) && dayData.records.length > 0)
      );
      
      // Kiểm tra xem có checkin record không (chỉ hiển thị "Chưa ra" nếu có checkin)
      let hasCheckin = false;
      let hasCheckout = false;
      let hasUnusedCheckout = false;
      
      if (dayData && dayData.records) {
        const checkinRecords = dayData.records.filter(r => {
          const type = (r.type || '').toString().trim();
          return type === 'Vào' || type === 'vào' || type.toLowerCase() === 'checkin' || type === 'vao';
        });
        const checkoutRecords = dayData.records.filter(r => {
          const type = (r.type || '').toString().trim();
          return type === 'Ra' || type === 'ra' || type.toLowerCase() === 'checkout';
        });
        
        hasCheckin = checkinRecords.length > 0;
        hasCheckout = checkoutRecords.length > 0;
        
        // Kiểm tra xem checkout có bị đánh dấu là đã dùng cho ngày trước không
        if (hasCheckout && dayData.usedCheckout && Array.isArray(dayData.usedCheckout)) {
          // Nếu tất cả checkout đều đã được dùng cho ngày trước, thì không có checkout thực sự
          const unusedCheckouts = checkoutRecords.filter(co => {
            return !dayData.usedCheckout.some(uc => uc === co || (uc.timestamp && co.timestamp && uc.timestamp === co.timestamp));
          });
          hasUnusedCheckout = unusedCheckouts.length > 0;
        } else {
          hasUnusedCheckout = hasCheckout;
        }
      }
      
      const workHours = dayData?.workHours !== undefined ? dayData.workHours : null;
      
      // Debug: Log cho tất cả các ngày có data hoặc có workHours
      if (dayData || workHours) {
        console.log(`[HistoryPage] Calendar day ${dateStr}:`, {
          hasDayData: !!dayData,
          hasRecords: !!(dayData && dayData.records),
          recordCount: dayData?.records?.length || 0,
          hasCheckin,
          hasCheckout,
          hasUnusedCheckout,
          usedCheckoutCount: dayData?.usedCheckout?.length || 0,
          workHours,
          willDisplay: hasCheckin && workHours !== null && workHours !== undefined && workHours !== ''
        });
      }
      
      // Chỉ hiển thị "Chưa ra" nếu có checkin nhưng không có checkout (hoặc checkout đã được dùng cho ngày trước)
      // Nếu không có checkin thì không hiển thị workHours
      // Nếu chỉ có checkout (không có checkin) và checkout chưa được dùng, cũng không hiển thị
      const displayWorkHours = (hasCheckin && workHours === 'Chưa ra') ? 'Chưa ra' : 
                                (hasCheckin && workHours && workHours !== 'Chưa ra') ? workHours : null;
      
      
      const dayOfWeek = new Date(year, month - 1, day).getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // Sunday or Saturday
      const isToday = dateStr === new Date().toISOString().slice(0, 10);
      
      days.push({ day, date: dateStr, hasAttendance, workHours: displayWorkHours, isWeekend, isToday, hasCheckin });
    }
    
    return days;
  }, [selectedMonth, attendanceData]);

  const [year, month] = selectedMonth.split('-').map(Number);
  const dayNames = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];

  return (
    <div className="history-page">
      {/* Top Bar */}
      <div className="calendar-top-bar">
        <button 
          className="btn-year"
          onClick={() => handleMonthChange(-12)}
        >
          ← {year}
        </button>
        <div className="top-bar-actions">
          {/* Có thể thêm các icon sau */}
        </div>
        </div>

      {/* Month Title & Stats */}
      <div className="month-title">
        <h1>Tháng {month}</h1>
        <div className="month-stats">
          <div className="stat-item">
            <span className="stat-label">Tổng giờ:</span>
            <span className="stat-value">{monthStats.totalHours}h</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Ngày làm việc:</span>
            <span className="stat-value">{monthStats.workingDays}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Chưa checkout:</span>
            <span className="stat-value warning">{monthStats.noCheckoutDays}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Chưa chấm công:</span>
            <span className="stat-value muted">{monthStats.noAttendanceDays}</span>
          </div>
        </div>
      </div>

      {/* Day Headers */}
      <div className="calendar-day-headers">
        {dayNames.map((dayName, index) => (
          <div 
            key={dayName} 
            className={`day-header ${index === 0 || index === 6 ? 'weekend' : ''}`}
          >
            {dayName}
        </div>
        ))}
              </div>

      {/* Calendar Grid */}
      <div className="calendar-grid">
        {calendarDays.map((dayData, index) => {
          if (dayData === null) {
            return <div key={`empty-${index}`} className="calendar-day empty"></div>;
          }

          const { day, date, hasAttendance, workHours, isWeekend, isToday, hasCheckin } = dayData;
      const status = getWorkHoursStatus(workHours);

          return (
            <div
              key={date}
              className={`calendar-day ${isWeekend ? 'weekend' : ''} ${isToday ? 'today' : ''} ${hasAttendance ? 'has-attendance' : ''} ${status.status}`}
              onClick={() => handleDateClick(date)}
            >
              <div className="day-number">{day}</div>
              {/* Chỉ hiển thị workHours nếu có checkin */}
              {hasCheckin && workHours !== null && workHours !== undefined && workHours !== '' && (
                <div 
                  className="work-hours" 
                  style={{ 
                    display: 'block', 
                    visibility: 'visible', 
                    opacity: 1,
                    color: status.color,
                    fontWeight: '600'
                  }}
                >
                  {typeof workHours === 'string' && workHours.includes('Chưa') ? 'Chưa ra' : `${workHours}h`}
                  </div>
                )}
            </div>
          );
        })}
              </div>

      {/* Month Navigation */}
      <div className="month-navigation">
        <button onClick={() => handleMonthChange(-1)} className="btn-nav">
          ← Tháng trước
        </button>
        <input
          type="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="month-input"
        />
        <button onClick={() => handleMonthChange(1)} className="btn-nav">
          Tháng sau →
        </button>
                    </div>

      {loading && (
        <div className="loading-overlay">
          <div className="loading">Đang tải...</div>
        </div>
      )}

      {error && (
        <div className="error-message">
          <p>⚠️ {error}</p>
        </div>
      )}

      {/* Modal chi tiết ngày */}
      {selectedDate && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {(() => {
                  // Parse date theo local timezone để tránh lệch ngày
                  const [year, month, day] = selectedDate.split('-').map(Number);
                  const dateObj = new Date(year, month - 1, day);
                  return dateObj.toLocaleDateString('vi-VN', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  });
                })()}
              </h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              {(() => {
                const { checkin, checkout } = getDayRecords(selectedDate);
                const dayData = attendanceData[selectedDate];
                const workHours = dayData?.workHours;
                const status = getWorkHoursStatus(workHours);

                return (
                  <>
                    {/* Thông tin tổng quan */}
                    <div className="day-summary">
                      <div className="summary-item">
                        <span className="summary-label">Số giờ làm việc:</span>
                        <span className="summary-value" style={{ color: status.color }}>
                          {workHours === 'Chưa ra' ? 'Chưa ra' : workHours ? `${workHours}h` : 'N/A'}
                        </span>
                      </div>
                      <div className="summary-item">
                        <span className="summary-label">Trạng thái:</span>
                        <span className="summary-value" style={{ color: status.color }}>
                          {status.status === 'enough' ? '✓ Đủ giờ' : 
                           status.status === 'partial' ? '⚠ Thiếu giờ' :
                           status.status === 'low' ? '✗ Thiếu nhiều' :
                           status.status === 'no-checkout' ? '⏳ Chưa checkout' : '❓ Không xác định'}
                        </span>
                      </div>
                    </div>

                    {/* Chi tiết Check-in */}
                    {checkin && (
                      <div className="detail-section">
                        <h3 className="detail-title">✅ Chấm công vào</h3>
                        <div className="detail-info">
                          <div className="detail-row">
                            <span className="detail-label">Thời gian:</span>
                            <span className="detail-value">{formatDateTime(checkin.timestamp || checkin.time)}</span>
                          </div>
                          {checkin.address && (
                            <div className="detail-row">
                              <span className="detail-label">Địa chỉ:</span>
                              <span className="detail-value">📍 {checkin.address}</span>
                            </div>
                          )}
                          {checkin.explanation && (
                            <div className="detail-row">
                              <span className="detail-label">Giải trình:</span>
                              <span className="detail-value explanation">{checkin.explanation}</span>
                            </div>
                          )}
                          {checkin.note && (
                            <div className="detail-row">
                              <span className="detail-label">Ghi chú:</span>
                              <span className={`detail-value ${checkin.note.includes('⚠️') ? 'warning' : ''}`}>
                                {checkin.note}
                              </span>
                            </div>
                          )}
                          {checkin.photo_url && (() => {
                            const imageUrl = getImageUrl(checkin.photo_url);
                            return imageUrl ? (
                              <div className="detail-row">
                                <span className="detail-label">Ảnh:</span>
                                <div className="image-container">
                                  <img 
                                    src={imageUrl} 
                                    alt="Check-in" 
                                    className="detail-image" 
                                    onClick={() => openImageModal(imageUrl)}
                                    style={{ cursor: 'pointer' }}
                                    onError={(e) => {
                                      console.error('Error loading image:', imageUrl);
                                      // Thử fallback: mở link Google Drive trong tab mới
                                      const originalUrl = checkin.photo_url;
                                      if (originalUrl && originalUrl.includes('drive.google.com')) {
                                        e.target.style.display = 'none';
                                        const hintDiv = e.target.nextSibling;
                                        hintDiv.innerHTML = `<a href="${originalUrl}" target="_blank" rel="noopener noreferrer" style="color: #007aff; text-decoration: underline;">Click để mở ảnh trên Google Drive</a>`;
                                        hintDiv.style.cursor = 'pointer';
                                      } else {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.textContent = 'Không thể tải ảnh';
                                      }
                                    }}
                                  />
                                  <div className="image-hint">Click để xem ảnh to</div>
                                </div>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    )}

                    {/* Chi tiết Check-out */}
                    {checkout && (
                      <div className="detail-section">
                        <h3 className="detail-title">🚪 Chấm công ra</h3>
                        <div className="detail-info">
                          <div className="detail-row">
                            <span className="detail-label">Thời gian:</span>
                            <span className="detail-value">{formatDateTime(checkout.timestamp || checkout.time)}</span>
                          </div>
                          {checkout.address && (
                            <div className="detail-row">
                              <span className="detail-label">Địa chỉ:</span>
                              <span className="detail-value">📍 {checkout.address}</span>
                            </div>
                          )}
                          {checkout.explanation && (
                            <div className="detail-row">
                              <span className="detail-label">Giải trình:</span>
                              <span className="detail-value explanation">{checkout.explanation}</span>
                            </div>
                          )}
                          {checkout.note && (
                            <div className="detail-row">
                              <span className="detail-label">Ghi chú:</span>
                              <span className={`detail-value ${checkout.note.includes('⚠️') ? 'warning' : ''}`}>
                                {checkout.note}
                              </span>
                            </div>
                          )}
                          {checkout.photo_url && (() => {
                            const imageUrl = getImageUrl(checkout.photo_url);
                            return imageUrl ? (
                              <div className="detail-row">
                                <span className="detail-label">Ảnh:</span>
                                <div className="image-container">
                                  <img 
                                    src={imageUrl} 
                                    alt="Check-out" 
                                    className="detail-image" 
                                    onClick={() => openImageModal(imageUrl)}
                                    style={{ cursor: 'pointer' }}
                                    onError={(e) => {
                                      console.error('Error loading image:', imageUrl);
                                      // Thử fallback: mở link Google Drive trong tab mới
                                      const originalUrl = checkout.photo_url;
                                      if (originalUrl && originalUrl.includes('drive.google.com')) {
                                        e.target.style.display = 'none';
                                        const hintDiv = e.target.nextSibling;
                                        hintDiv.innerHTML = `<a href="${originalUrl}" target="_blank" rel="noopener noreferrer" style="color: #007aff; text-decoration: underline;">Click để mở ảnh trên Google Drive</a>`;
                                        hintDiv.style.cursor = 'pointer';
                                      } else {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.textContent = 'Không thể tải ảnh';
                                      }
                                    }}
                                  />
                                  <div className="image-hint">Click để xem ảnh to</div>
                                </div>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    )}

                    {!checkin && !checkout && (
                      <div className="no-details">
                        <p>Chưa có dữ liệu chấm công cho ngày này</p>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Modal xem ảnh to */}
      {selectedImage && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={closeImageModal}>×</button>
            <img src={selectedImage} alt="Full size" className="image-modal-image" />
          </div>
        </div>
      )}
    </div>
  );
}

export default HistoryPage;
